/* Custom cursor + trail catalog
 *
 * Cursors: small SVGs inlined as data URIs so there are no extra HTTP
 * requests. CSS `cursor: url(...) hotX hotY, auto` swaps them.
 *
 * Trails: lightweight "recipe" objects describing how the CursorTrail
 * component should spawn fading particles on mousemove. Each particle
 * is a small absolutely-positioned <span> with an inline emoji or
 * gradient; CSS handles the fade/transform.
 */

/* Helpers ------------------------------------------------------------ */

// 9 page-accent colors used throughout the Vault — pulled from real
// Tailwind tokens in the codebase so cursors and trails match the page
// they're hovering. Black cursors on a black UI are unreadable; every
// cursor in this catalog now defaults to one of these bright colors.
const ACCENTS = {
  red:     "ff3b30",  // Connect Dots / Archive
  yellow:  "ffd60a",  // The Ties / Awakening
  blue:    "60a5fa",  // Deep Dive
  purple:  "a78bfa",  // Threads / Profile theme studio
  cyan:    "22d3ee",  // War Room / Live sessions
  pink:    "ec4899",  // Equation / Stitches
  green:   "22c55e",  // Cards
  magenta: "ff2a8e",  // Y2K femme
  orange:  "f59e0b",  // Saved / Coins
};
const A = (k) => `#${ACCENTS[k]}`;

// Turns an inline SVG string into a `cursor: url(data:image/svg+xml;...)`
// value usable from CSS. The hotspot defaults to the center of a 32x32
// canvas which is what every cursor SVG in this file uses.
function svgCursor(svg, hotX = 16, hotY = 16) {
  // encodeURIComponent keeps it data-URI-safe across all browsers.
  const enc = encodeURIComponent(svg)
    .replace(/'/g, "%27").replace(/"/g, "%22");
  return `url("data:image/svg+xml;utf8,${enc}") ${hotX} ${hotY}, auto`;
}

// Drop-in 32x32 viewbox wrapper
const wrap = (inner) =>
  `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>${inner}</svg>`;

/* ===== CURSORS (18) ================================================ */

export const CURSORS = [
  {
    id: "default",
    name: "System Default",
    css: "auto",
  },
  {
    id: "crosshair_target",
    name: "Classified Reticle",
    css: svgCursor(wrap(
      "<circle cx='16' cy='16' r='12' fill='none' stroke='#ff3b30' stroke-width='1.5'/>" +
      "<line x1='16' y1='2' x2='16' y2='10' stroke='#ff3b30' stroke-width='1.5'/>" +
      "<line x1='16' y1='22' x2='16' y2='30' stroke='#ff3b30' stroke-width='1.5'/>" +
      "<line x1='2' y1='16' x2='10' y2='16' stroke='#ff3b30' stroke-width='1.5'/>" +
      "<line x1='22' y1='16' x2='30' y2='16' stroke='#ff3b30' stroke-width='1.5'/>" +
      "<circle cx='16' cy='16' r='1.5' fill='#ff3b30'/>"
    )),
  },
  {
    id: "all_seeing_eye",
    name: "All-Seeing Eye",
    css: svgCursor(wrap(
      "<path d='M2 16 Q16 4 30 16 Q16 28 2 16Z' fill='#ffd60a' stroke='#fff' stroke-width='1'/>" +
      "<circle cx='16' cy='16' r='5' fill='#000'/>" +
      "<circle cx='16' cy='16' r='2' fill='#ffd60a'/>"
    )),
  },
  {
    id: "q_glyph",
    name: "Q Glyph",
    css: svgCursor(wrap(
      "<circle cx='16' cy='16' r='10' fill='none' stroke='#ffd60a' stroke-width='3'/>" +
      "<line x1='20' y1='22' x2='28' y2='30' stroke='#ffd60a' stroke-width='3' stroke-linecap='round'/>"
    )),
  },
  {
    id: "pyramid_eye",
    name: "Pyramid Eye",
    css: svgCursor(wrap(
      "<polygon points='16,3 30,28 2,28' fill='#ffd60a' stroke='#fff' stroke-width='1'/>" +
      "<circle cx='16' cy='20' r='4' fill='#000'/>" +
      "<circle cx='16' cy='20' r='1.5' fill='#fff'/>"
    )),
  },
  {
    id: "pink_skull",
    name: "Pink Skull",
    css: svgCursor(wrap(
      "<circle cx='16' cy='14' r='10' fill='#ff2a8e'/>" +
      "<circle cx='12' cy='13' r='2.5' fill='#000'/>" +
      "<circle cx='20' cy='13' r='2.5' fill='#000'/>" +
      "<rect x='13' y='19' width='2' height='3' fill='#000'/>" +
      "<rect x='17' y='19' width='2' height='3' fill='#000'/>"
    )),
  },
  {
    id: "green_skull",
    name: "Green Skull",
    css: svgCursor(wrap(
      "<circle cx='16' cy='14' r='10' fill='#00ff66'/>" +
      "<circle cx='12' cy='13' r='2.5' fill='#000'/>" +
      "<circle cx='20' cy='13' r='2.5' fill='#000'/>" +
      "<rect x='13' y='19' width='2' height='3' fill='#000'/>" +
      "<rect x='17' y='19' width='2' height='3' fill='#000'/>"
    )),
  },
  {
    id: "ufo_saucer",
    name: "UFO Saucer",
    css: svgCursor(wrap(
      "<ellipse cx='16' cy='20' rx='13' ry='4' fill='#a78bfa'/>" +
      "<ellipse cx='16' cy='15' rx='8' ry='5' fill='#60a5fa'/>" +
      "<circle cx='12' cy='20' r='1.2' fill='#ffd60a'/>" +
      "<circle cx='16' cy='20' r='1.2' fill='#ffd60a'/>" +
      "<circle cx='20' cy='20' r='1.2' fill='#ffd60a'/>"
    )),
  },
  {
    id: "magnifier",
    name: "Investigator's Lens",
    css: svgCursor(wrap(
      "<circle cx='13' cy='13' r='8' fill='none' stroke='#f5f5f5' stroke-width='2'/>" +
      "<circle cx='13' cy='13' r='5' fill='rgba(96,165,250,0.3)'/>" +
      "<line x1='19' y1='19' x2='29' y2='29' stroke='#f5f5f5' stroke-width='3' stroke-linecap='round'/>"
    ), 13, 13),
  },
  {
    id: "pentagram",
    name: "Pentagram",
    css: svgCursor(wrap(
      "<polygon points='16,3 19,13 30,13 21,20 24,30 16,24 8,30 11,20 2,13 13,13' fill='#ec4899' stroke='#fff' stroke-width='0.5'/>"
    )),
  },
  {
    id: "ankh",
    name: "Ankh",
    css: svgCursor(wrap(
      "<circle cx='16' cy='10' r='5' fill='none' stroke='#ffd60a' stroke-width='3'/>" +
      "<line x1='16' y1='15' x2='16' y2='29' stroke='#ffd60a' stroke-width='3'/>" +
      "<line x1='9' y1='20' x2='23' y2='20' stroke='#ffd60a' stroke-width='3'/>"
    )),
  },
  {
    id: "laser_dot",
    name: "Red Laser Dot",
    css: svgCursor(wrap(
      "<circle cx='16' cy='16' r='4' fill='#ff3b30'/>" +
      "<circle cx='16' cy='16' r='7' fill='none' stroke='#ff3b30' stroke-width='1' opacity='0.4'/>"
    )),
  },
  {
    id: "lightning",
    name: "Lightning Bolt",
    css: svgCursor(wrap(
      "<polygon points='18,2 8,17 14,17 10,30 24,13 17,13 22,2' fill='#ffd60a' stroke='#fff' stroke-width='0.8'/>"
    )),
  },
  {
    id: "heart_y2k",
    name: "Y2K Heart",
    css: svgCursor(wrap(
      "<path d='M16 28 C 4 19, 4 8, 12 8 C 16 8, 16 12, 16 12 C 16 12, 16 8, 20 8 C 28 8, 28 19, 16 28Z' fill='#ff2a8e' stroke='#fff' stroke-width='0.8'/>" +
      "<circle cx='12' cy='12' r='2' fill='#fff' opacity='0.7'/>"
    )),
  },
  {
    id: "spider",
    name: "Cyan Spider",
    css: svgCursor(wrap(
      // Recolored from black → cyan so it shows up on the dark UI.
      `<ellipse cx='16' cy='17' rx='6' ry='5' fill='${A("cyan")}'/>` +
      `<line x1='10' y1='14' x2='2' y2='8' stroke='${A("cyan")}' stroke-width='1.5'/>` +
      `<line x1='10' y1='17' x2='2' y2='17' stroke='${A("cyan")}' stroke-width='1.5'/>` +
      `<line x1='10' y1='20' x2='2' y2='26' stroke='${A("cyan")}' stroke-width='1.5'/>` +
      `<line x1='22' y1='14' x2='30' y2='8' stroke='${A("cyan")}' stroke-width='1.5'/>` +
      `<line x1='22' y1='17' x2='30' y2='17' stroke='${A("cyan")}' stroke-width='1.5'/>` +
      `<line x1='22' y1='20' x2='30' y2='26' stroke='${A("cyan")}' stroke-width='1.5'/>` +
      `<circle cx='13' cy='15' r='1' fill='${A("red")}'/><circle cx='19' cy='15' r='1' fill='${A("red")}'/>`
    )),
  },
  {
    id: "owl_athena",
    name: "Owl of Athena",
    css: svgCursor(wrap(
      "<ellipse cx='16' cy='17' rx='8' ry='10' fill='#d4a455'/>" +
      `<circle cx='12' cy='14' r='4' fill='#fff'/><circle cx='12' cy='14' r='2' fill='${A("red")}'/>` +
      `<circle cx='20' cy='14' r='4' fill='#fff'/><circle cx='20' cy='14' r='2' fill='${A("red")}'/>` +
      `<polygon points='16,17 14,20 18,20' fill='${A("yellow")}'/>`
    )),
  },
  {
    id: "rainbow_star",
    name: "Rainbow Shooting Star",
    css: svgCursor(wrap(
      "<defs><linearGradient id='rs' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='#ff0000'/><stop offset='0.5' stop-color='#ffee00'/><stop offset='1' stop-color='#00ccff'/></linearGradient></defs>" +
      "<polygon points='16,3 19,13 30,13 21,20 24,30 16,24 8,30 11,20 2,13 13,13' fill='url(#rs)'/>"
    )),
  },
  {
    id: "vault_coin",
    name: "Vault Coin",
    css: svgCursor(wrap(
      `<circle cx='16' cy='16' r='12' fill='${A("yellow")}' stroke='#a87900' stroke-width='1.5'/>` +
      `<text x='16' y='21' font-family='monospace' font-size='14' font-weight='bold' fill='#a87900' text-anchor='middle'>V</text>`
    )),
  },
  {
    id: "redacted_marker",
    name: "Redacted Marker",
    css: svgCursor(wrap(
      // Beefier redaction bar — solid red fill, taller so it's actually
      // visible on the dark Vault UI.
      `<rect x='2' y='10' width='28' height='12' fill='${A("red")}' stroke='#fff' stroke-width='1'/>`
    )),
  },

  // --- BATCH 2 — Page-accent colored cursors (added Feb 2026) ----------- //
  {
    id: "arrow_red",
    name: "Red Arrow",
    css: svgCursor(wrap(
      `<polygon points='4,4 4,24 11,18 16,28 21,26 16,16 24,16' fill='${A("red")}' stroke='#fff' stroke-width='0.7'/>`
    ), 4, 4),
  },
  {
    id: "arrow_yellow",
    name: "Yellow Arrow",
    css: svgCursor(wrap(
      `<polygon points='4,4 4,24 11,18 16,28 21,26 16,16 24,16' fill='${A("yellow")}' stroke='#fff' stroke-width='0.7'/>`
    ), 4, 4),
  },
  {
    id: "arrow_blue",
    name: "Blue Arrow",
    css: svgCursor(wrap(
      `<polygon points='4,4 4,24 11,18 16,28 21,26 16,16 24,16' fill='${A("blue")}' stroke='#fff' stroke-width='0.7'/>`
    ), 4, 4),
  },
  {
    id: "arrow_cyan",
    name: "Cyan Arrow",
    css: svgCursor(wrap(
      `<polygon points='4,4 4,24 11,18 16,28 21,26 16,16 24,16' fill='${A("cyan")}' stroke='#fff' stroke-width='0.7'/>`
    ), 4, 4),
  },
  {
    id: "arrow_green",
    name: "Green Arrow",
    css: svgCursor(wrap(
      `<polygon points='4,4 4,24 11,18 16,28 21,26 16,16 24,16' fill='${A("green")}' stroke='#fff' stroke-width='0.7'/>`
    ), 4, 4),
  },
  {
    id: "diamond_pink",
    name: "Pink Diamond",
    css: svgCursor(wrap(
      `<polygon points='16,3 28,16 16,29 4,16' fill='${A("magenta")}' stroke='#fff' stroke-width='1'/>` +
      `<polygon points='16,6 24,16 16,26 8,16' fill='${A("pink")}'/>`
    )),
  },
  {
    id: "diamond_cyan",
    name: "Cyan Diamond",
    css: svgCursor(wrap(
      `<polygon points='16,3 28,16 16,29 4,16' fill='${A("cyan")}' stroke='#fff' stroke-width='1'/>`
    )),
  },
  {
    id: "ring_orange",
    name: "Orange Ring",
    css: svgCursor(wrap(
      `<circle cx='16' cy='16' r='12' fill='none' stroke='${A("orange")}' stroke-width='3'/>` +
      `<circle cx='16' cy='16' r='3' fill='${A("orange")}'/>`
    )),
  },
  {
    id: "ring_purple",
    name: "Purple Ring",
    css: svgCursor(wrap(
      `<circle cx='16' cy='16' r='12' fill='none' stroke='${A("purple")}' stroke-width='3'/>` +
      `<circle cx='16' cy='16' r='3' fill='${A("purple")}'/>`
    )),
  },
  {
    id: "ring_green",
    name: "Green Reticle",
    css: svgCursor(wrap(
      `<circle cx='16' cy='16' r='12' fill='none' stroke='${A("green")}' stroke-width='1.5'/>` +
      `<line x1='16' y1='4' x2='16' y2='12' stroke='${A("green")}' stroke-width='1.5'/>` +
      `<line x1='16' y1='20' x2='16' y2='28' stroke='${A("green")}' stroke-width='1.5'/>` +
      `<line x1='4' y1='16' x2='12' y2='16' stroke='${A("green")}' stroke-width='1.5'/>` +
      `<line x1='20' y1='16' x2='28' y2='16' stroke='${A("green")}' stroke-width='1.5'/>` +
      `<circle cx='16' cy='16' r='1.5' fill='${A("green")}'/>`
    )),
  },
  {
    id: "star_yellow",
    name: "Yellow Star",
    css: svgCursor(wrap(
      `<polygon points='16,3 19,13 30,13 21,20 24,30 16,24 8,30 11,20 2,13 13,13' fill='${A("yellow")}' stroke='#fff' stroke-width='0.6'/>`
    )),
  },
  {
    id: "star_pink",
    name: "Pink Star",
    css: svgCursor(wrap(
      `<polygon points='16,3 19,13 30,13 21,20 24,30 16,24 8,30 11,20 2,13 13,13' fill='${A("magenta")}' stroke='#fff' stroke-width='0.6'/>`
    )),
  },
  {
    id: "flame_orange",
    name: "Orange Flame",
    css: svgCursor(wrap(
      `<path d='M16 4 C 20 10, 26 14, 24 22 C 22 28, 10 28, 8 22 C 6 14, 12 10, 16 4Z' fill='${A("orange")}'/>` +
      `<path d='M16 11 C 18 14, 21 17, 20 22 C 18 26, 14 26, 12 22 C 11 17, 14 14, 16 11Z' fill='${A("yellow")}'/>`
    )),
  },
  {
    id: "moon_purple",
    name: "Purple Crescent",
    css: svgCursor(wrap(
      `<path d='M22 6 A 12 12 0 1 0 22 26 A 9 9 0 1 1 22 6Z' fill='${A("purple")}' stroke='#fff' stroke-width='0.7'/>`
    )),
  },
  {
    id: "lock_red",
    name: "Red Padlock",
    css: svgCursor(wrap(
      `<rect x='8' y='14' width='16' height='14' rx='2' fill='${A("red")}'/>` +
      `<path d='M11 14 V10 A 5 5 0 0 1 21 10 V14' fill='none' stroke='${A("red")}' stroke-width='2'/>` +
      `<circle cx='16' cy='20' r='2' fill='#000'/>`
    )),
  },
  {
    id: "wand_pink",
    name: "Magic Wand",
    css: svgCursor(wrap(
      `<line x1='4' y1='28' x2='22' y2='10' stroke='${A("purple")}' stroke-width='3' stroke-linecap='round'/>` +
      `<line x1='5' y1='29' x2='23' y2='11' stroke='${A("pink")}' stroke-width='1.5' stroke-linecap='round'/>` +
      `<polygon points='24,4 27,11 30,8 27,13 30,17 24,15 19,17 21,12 18,8 23,9' fill='${A("yellow")}'/>`
    ), 4, 28),
  },
  {
    id: "alien_lime",
    name: "Lime Alien Head",
    css: svgCursor(wrap(
      `<ellipse cx='16' cy='17' rx='9' ry='11' fill='${A("green")}'/>` +
      `<ellipse cx='12' cy='17' rx='2' ry='4' fill='#000'/>` +
      `<ellipse cx='20' cy='17' rx='2' ry='4' fill='#000'/>`
    )),
  },
  {
    id: "bolt_cyan",
    name: "Cyan Bolt",
    css: svgCursor(wrap(
      `<polygon points='18,2 8,17 14,17 10,30 24,13 17,13 22,2' fill='${A("cyan")}' stroke='#fff' stroke-width='0.7'/>`
    )),
  },
  {
    id: "drop_blue",
    name: "Water Droplet",
    css: svgCursor(wrap(
      `<path d='M16 3 C 22 12, 26 18, 26 22 A 10 10 0 0 1 6 22 C 6 18, 10 12, 16 3Z' fill='${A("blue")}' stroke='#fff' stroke-width='0.6'/>` +
      `<ellipse cx='13' cy='20' rx='2.5' ry='3' fill='#fff' opacity='0.55'/>`
    )),
  },
  {
    id: "key_yellow",
    name: "Golden Key",
    css: svgCursor(wrap(
      `<circle cx='8' cy='16' r='5' fill='none' stroke='${A("yellow")}' stroke-width='3'/>` +
      `<rect x='13' y='14.5' width='15' height='3' fill='${A("yellow")}'/>` +
      `<rect x='23' y='17' width='3' height='4' fill='${A("yellow")}'/>` +
      `<rect x='27' y='17' width='2' height='3' fill='${A("yellow")}'/>`
    ), 8, 16),
  },
  {
    id: "spinner_purple",
    name: "Spinner",
    css: svgCursor(wrap(
      `<circle cx='16' cy='16' r='10' fill='none' stroke='${A("purple")}' stroke-width='3' stroke-dasharray='14 8'/>` +
      `<circle cx='16' cy='16' r='2' fill='${A("purple")}'/>`
    )),
  },
  {
    id: "heart_blue",
    name: "Blue Heart",
    css: svgCursor(wrap(
      `<path d='M16 28 C 4 19, 4 8, 12 8 C 16 8, 16 12, 16 12 C 16 12, 16 8, 20 8 C 28 8, 28 19, 16 28Z' fill='${A("blue")}' stroke='#fff' stroke-width='0.8'/>` +
      `<circle cx='12' cy='12' r='2' fill='#fff' opacity='0.7'/>`
    )),
  },
  {
    id: "eyeball_red",
    name: "Bloodshot Eye",
    css: svgCursor(wrap(
      `<ellipse cx='16' cy='16' rx='13' ry='8' fill='#fff' stroke='${A("red")}' stroke-width='1'/>` +
      `<circle cx='16' cy='16' r='5' fill='${A("blue")}'/>` +
      `<circle cx='16' cy='16' r='2.5' fill='#000'/>` +
      `<line x1='3' y1='14' x2='6' y2='16' stroke='${A("red")}' stroke-width='0.5'/>` +
      `<line x1='29' y1='18' x2='26' y2='16' stroke='${A("red")}' stroke-width='0.5'/>`
    )),
  },
  {
    id: "music_note_magenta",
    name: "Music Note",
    css: svgCursor(wrap(
      `<ellipse cx='10' cy='24' rx='5' ry='4' fill='${A("magenta")}'/>` +
      `<rect x='14' y='6' width='2.5' height='20' fill='${A("magenta")}'/>` +
      `<path d='M14 6 Q 24 8, 26 16 Q 18 12, 14 16Z' fill='${A("magenta")}'/>`
    ), 10, 24),
  },
  {
    id: "puzzle_orange",
    name: "Puzzle Piece",
    css: svgCursor(wrap(
      `<path d='M4 8 H 14 A 3 3 0 0 1 14 14 H 18 A 3 3 0 0 0 18 8 H 28 V 18 A 3 3 0 0 1 22 18 V 22 A 3 3 0 0 0 28 22 V 28 H 18 A 3 3 0 0 1 18 22 V 18 A 3 3 0 0 0 14 18 H 4Z' fill='${A("orange")}' stroke='#fff' stroke-width='0.5'/>`
    )),
  },
  {
    id: "biohazard_green",
    name: "Biohazard",
    css: svgCursor(wrap(
      `<circle cx='16' cy='16' r='3' fill='${A("green")}'/>` +
      `<path d='M16 4 C 19 9, 19 12, 16 13 C 13 12, 13 9, 16 4Z' fill='${A("green")}'/>` +
      `<path d='M6 24 C 11 21, 14 21, 15 19 C 14 17, 11 17, 6 20Z' fill='${A("green")}'/>` +
      `<path d='M26 24 C 21 21, 18 21, 17 19 C 18 17, 21 17, 26 20Z' fill='${A("green")}'/>`
    )),
  },
];

export const CURSOR_IDS = new Set(CURSORS.map(c => c.id));

/* ===== TRAILS (8) ================================================== */
// Each trail recipe describes how the CursorTrail component should spawn
// particles. The component reads { count, color, glyph?, size?, life }
// and spawns a fading <span> on every mousemove event.
export const TRAILS = [
  { id: "none",         name: "None — Clean",       recipe: null },
  { id: "rainbow",      name: "Rainbow Trail",      recipe: { kind: "rainbow", count: 1, size: 6,  life: 700 } },
  { id: "sparkle",      name: "Sparkle Stars",      recipe: { kind: "glyph",   count: 1, glyph: "✦", color: "#ffd60a", size: 14, life: 800 } },
  { id: "glow",         name: "Violet Glow",        recipe: { kind: "blob",    count: 1, color: "#a78bfa", size: 22, life: 600 } },
  { id: "fallen_stars", name: "Falling Stars",      recipe: { kind: "glyph",   count: 1, glyph: "★", color: "#ffffff", size: 12, life: 900 } },
  { id: "static_dots",  name: "TV Static",          recipe: { kind: "dots",    count: 4, color: "#ffffff", size: 2,  life: 450 } },
  { id: "redact_bars",  name: "Redaction Bars",     recipe: { kind: "bar",     color: "#1a1a1a", size: 18, life: 600 } },
  { id: "creepy_eyes",  name: "Watching Eyes",      recipe: { kind: "glyph",   count: 1, glyph: "👁", color: "#ffd60a", size: 14, life: 1000 } },
  { id: "pink_hearts",  name: "Pink Hearts",        recipe: { kind: "glyph",   count: 1, glyph: "♥", color: "#ff2a8e", size: 14, life: 800 } },
  { id: "green_matrix", name: "Matrix Rain",        recipe: { kind: "glyph",   count: 1, glyph: "01", color: "#00ff66", size: 11, life: 700 } },
  { id: "ufo_dots",     name: "UFO Plasma",         recipe: { kind: "blob",    count: 1, color: "#60a5fa", size: 16, life: 500 } },

  // --- BATCH 2 — added Feb 2026 (matches page-accent colors) ---------- //
  { id: "cyan_glow",    name: "Cyan Plasma",        recipe: { kind: "blob",    count: 1, color: "#22d3ee", size: 20, life: 600 } },
  { id: "red_glow",     name: "Red Pulse",          recipe: { kind: "blob",    count: 1, color: "#ff3b30", size: 18, life: 550 } },
  { id: "yellow_glow",  name: "Solar Flare",        recipe: { kind: "blob",    count: 1, color: "#ffd60a", size: 18, life: 600 } },
  { id: "orange_glow",  name: "Ember Wake",         recipe: { kind: "blob",    count: 1, color: "#f59e0b", size: 20, life: 700 } },
  { id: "green_glow",   name: "Lime Wake",          recipe: { kind: "blob",    count: 1, color: "#22c55e", size: 18, life: 550 } },
  { id: "fire",         name: "Fire Trail",         recipe: { kind: "glyph",   count: 1, glyph: "🔥", color: "#ff7a00", size: 16, life: 700 } },
  { id: "snow",         name: "Snowflakes",         recipe: { kind: "glyph",   count: 1, glyph: "❄", color: "#a5f3fc", size: 14, life: 1100 } },
  { id: "bubbles",      name: "Bubbles",            recipe: { kind: "glyph",   count: 1, glyph: "○", color: "#60a5fa", size: 14, life: 1200 } },
  { id: "lightning",    name: "Lightning",          recipe: { kind: "glyph",   count: 1, glyph: "⚡", color: "#ffd60a", size: 16, life: 500 } },
  { id: "skulls",       name: "Skull Trail",        recipe: { kind: "glyph",   count: 1, glyph: "💀", color: "#fff", size: 14, life: 800 } },
  { id: "ghosts",       name: "Phantom Trail",      recipe: { kind: "glyph",   count: 1, glyph: "👻", color: "#fff", size: 16, life: 900 } },
  { id: "rose",         name: "Rose Petals",        recipe: { kind: "glyph",   count: 1, glyph: "🌹", color: "#ff2a8e", size: 14, life: 900 } },
  { id: "music_notes",  name: "Music Notes",        recipe: { kind: "glyph",   count: 1, glyph: "♪", color: "#a78bfa", size: 14, life: 800 } },
  { id: "ufos",         name: "UFO Squadron",       recipe: { kind: "glyph",   count: 1, glyph: "🛸", color: "#60a5fa", size: 14, life: 700 } },
  { id: "eyes_pink",    name: "Pink Eyes",          recipe: { kind: "glyph",   count: 1, glyph: "👁", color: "#ff2a8e", size: 14, life: 900 } },
  { id: "question",     name: "Question Marks",     recipe: { kind: "glyph",   count: 1, glyph: "?", color: "#ffd60a", size: 16, life: 700 } },
  { id: "money",        name: "Coin Trail",         recipe: { kind: "glyph",   count: 1, glyph: "$", color: "#ffd60a", size: 16, life: 700 } },
  { id: "leaves",       name: "Falling Leaves",     recipe: { kind: "glyph",   count: 1, glyph: "🍂", color: "#f59e0b", size: 14, life: 1100 } },
  { id: "blood",        name: "Blood Drips",        recipe: { kind: "glyph",   count: 1, glyph: "🩸", color: "#ff3b30", size: 12, life: 950 } },
  { id: "moons",        name: "Crescent Moons",     recipe: { kind: "glyph",   count: 1, glyph: "☾", color: "#a78bfa", size: 14, life: 900 } },
  { id: "stars_yellow", name: "Gold Star Burst",    recipe: { kind: "glyph",   count: 1, glyph: "★", color: "#ffd60a", size: 14, life: 900 } },

  // --- BATCH 3 — added Feb 2026 (more variety) ---------- //
  { id: "diamonds",     name: "Diamond Trail",      recipe: { kind: "glyph",   count: 1, glyph: "◆", color: "#22d3ee", size: 14, life: 800 } },
  { id: "checks",       name: "Check Marks",        recipe: { kind: "glyph",   count: 1, glyph: "✓", color: "#22c55e", size: 14, life: 700 } },
  { id: "xs",           name: "X Marks",            recipe: { kind: "glyph",   count: 1, glyph: "✕", color: "#ff3b30", size: 14, life: 700 } },
  { id: "arrows",       name: "Arrow Streak",       recipe: { kind: "glyph",   count: 1, glyph: "→", color: "#a78bfa", size: 14, life: 600 } },
  { id: "infinity",     name: "Infinity",           recipe: { kind: "glyph",   count: 1, glyph: "∞", color: "#22d3ee", size: 16, life: 900 } },
  { id: "atoms",        name: "Atoms",              recipe: { kind: "glyph",   count: 1, glyph: "⚛", color: "#a78bfa", size: 16, life: 900 } },
  { id: "yin_yang",     name: "Yin / Yang",         recipe: { kind: "glyph",   count: 1, glyph: "☯", color: "#fff", size: 14, life: 900 } },
  { id: "peace",        name: "Peace Signs",        recipe: { kind: "glyph",   count: 1, glyph: "☮", color: "#22c55e", size: 14, life: 900 } },
  { id: "skull_orange", name: "Orange Skulls",      recipe: { kind: "glyph",   count: 1, glyph: "💀", color: "#f59e0b", size: 14, life: 800 } },
  { id: "alien",        name: "Alien Heads",        recipe: { kind: "glyph",   count: 1, glyph: "👽", color: "#22c55e", size: 14, life: 800 } },
  { id: "crowns",       name: "Crowns",             recipe: { kind: "glyph",   count: 1, glyph: "👑", color: "#ffd60a", size: 14, life: 800 } },
  { id: "butterfly",    name: "Butterflies",        recipe: { kind: "glyph",   count: 1, glyph: "🦋", color: "#60a5fa", size: 14, life: 1000 } },
  { id: "smoke",        name: "Smoke Puffs",        recipe: { kind: "glyph",   count: 1, glyph: "💨", color: "#fff", size: 14, life: 900 } },
  { id: "sparks",       name: "Sparks",             recipe: { kind: "glyph",   count: 1, glyph: "✨", color: "#ffd60a", size: 14, life: 700 } },
  { id: "comet",        name: "Comet Tail",         recipe: { kind: "glyph",   count: 1, glyph: "☄", color: "#ff7a00", size: 14, life: 700 } },
  { id: "snake",        name: "Snake Trail",        recipe: { kind: "glyph",   count: 1, glyph: "🐍", color: "#22c55e", size: 14, life: 900 } },
  { id: "magenta_pulse","name": "Magenta Pulse",    recipe: { kind: "blob",    count: 1, color: "#ff2a8e", size: 22, life: 600 } },
  { id: "white_orbs",   name: "White Orbs",         recipe: { kind: "blob",    count: 1, color: "#ffffff", size: 14, life: 800 } },
  { id: "crystals",     name: "Crystals",           recipe: { kind: "glyph",   count: 1, glyph: "💎", color: "#22d3ee", size: 14, life: 800 } },
];

export const TRAIL_IDS = new Set(TRAILS.map(t => t.id));
